import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import ModalCloseButton from './ui/ModalCloseButton';
import { getIntentCtaLabel } from '../utils/planningIntent';


// Per-category metadata: icon emoji + description
const CATEGORY_META = {
  'Decor':          { icon: '🎨', desc: 'Centerpieces, lighting, banners & more' },
  'Food & Cake':    { icon: '🎂', desc: 'Catering, cake, drinks & desserts' },
  'Activities':     { icon: '🎯', desc: 'Games, entertainment & experiences' },
  'Printables':     { icon: '🖨️', desc: 'Invites, place cards & signage' },
  'Rentals':        { icon: '🪑', desc: 'Tables, chairs, linens & AV gear' },
  'Party Supplies': { icon: '🎉', desc: 'Plates, cups, balloons & favors' },
};

const defaultCategories = Object.keys(CATEGORY_META);

// Derive a smart suggested budget split from the selected categories
const suggestSplit = (budget, categories) => {
  const weights = {
    'Decor':          0.30,
    'Food & Cake':    0.35,
    'Activities':     0.10,
    'Printables':     0.05,
    'Rentals':        0.12,
    'Party Supplies': 0.08,
  };
  const activeWeights = categories.reduce((acc, c) => acc + (weights[c] || 0), 0);
  if (activeWeights === 0) return {};
  const split = {};
  let currentTotal = 0;
  
  categories.forEach((c, idx) => {
    if (idx === categories.length - 1) {
      // Last category gets the remainder to avoid $1 off rounding issues
      split[c] = Math.max(0, budget - currentTotal);
    } else {
      const val = Math.round(budget * (weights[c] || 0) / activeWeights);
      split[c] = val;
      currentTotal += val;
    }
  });
  return split;
};

const ThemeSelectionModal = ({ isOpen, onClose, onConfirm, initialPreferences, eventData, theme }) => {
  const formatPrice = (amount) => formatCurrency(amount || 0, eventData?.country || 'US');
  const currencySymbol = useMemo(() => getCurrencySymbol(eventData?.country || 'US'), [eventData?.country]);

  const budget = eventData?.budget || 0;
  const tolerance = eventData?.budget_tolerance_percent || 0;
  const upperBound = budget * (1 + tolerance / 100);
  const lowerBound = budget * (1 - tolerance / 100);

  const initialState = useMemo(() => {
    let includeCategories = defaultCategories;
    if (initialPreferences?.includeCategories?.length) {
      includeCategories = initialPreferences.includeCategories;
    } else if (eventData?.focus_selection?.length && !eventData.focus_selection.includes('everything')) {
      const focus = eventData.focus_selection;
      const newIncludes = new Set();
      if (focus.includes('decor') || focus.includes('theme_ideas')) {
        ['Decor','Printables','Party Supplies','Rentals'].forEach(c => newIncludes.add(c));
      }
      if (focus.includes('food')) {
        newIncludes.add('Food & Cake');
        newIncludes.add('Party Supplies');
      }
      if (focus.includes('activities')) {
        newIncludes.add('Activities');
        newIncludes.add('Party Supplies');
      }
      if (newIncludes.size > 0) includeCategories = defaultCategories.filter(c => newIncludes.has(c));
    }
    return {
      includeCategories,
      categoryBudgets: initialPreferences?.categoryBudgets || {},
      tweakNotes: initialPreferences?.tweakNotes || '',
    };
  }, [initialPreferences, eventData]);

   const [includeCategories, setIncludeCategories] = useState(initialState.includeCategories);
   const [categoryBudgets, setCategoryBudgets] = useState(initialState.categoryBudgets);
   const [tweakNotes, setTweakNotes] = useState(initialState.tweakNotes);
   const [existingItems, setExistingItems] = useState(initialPreferences?.existingItems || '');
   const [showOverBudgetConfirm, setShowOverBudgetConfirm] = useState(false);
   const [pendingConfirm, setPendingConfirm] = useState(null);
   const [splitMode, setSplitMode] = useState('amount'); // 'amount' or 'percentage'

  // Manage body class for modal stacking context
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [isOpen]);

  useEffect(() => {
    setIncludeCategories(initialState.includeCategories);
    setCategoryBudgets(initialState.categoryBudgets);
    setTweakNotes(initialState.tweakNotes);
    setExistingItems(initialPreferences?.existingItems || '');
  }, [initialState, initialPreferences]);

  const assignedTotal = Object.values(categoryBudgets).reduce((s, v) => s + (Number(v) || 0), 0);
  const remaining = budget - assignedTotal;
  const overBy = assignedTotal - upperBound;
  const budgetPct = budget > 0 ? Math.min(100, Math.round((assignedTotal / budget) * 100)) : 0;

  const budgetStatus = (() => {
    if (assignedTotal > upperBound) return { label: `Over by ${formatPrice(overBy)}`, color: '#ef4444', bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200' };
    if (assignedTotal >= lowerBound && assignedTotal > 0) return { label: 'Within budget ✓', color: '#22c55e', bg: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-200' };
    if (assignedTotal > 0) return { label: 'Under budget', color: '#f59e0b', bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' };
    return null;
  })();

  const handleToggleInclude = (cat) =>
    setIncludeCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );

  const handleBudgetChange = (cat, val) => {
    let num;
    if (splitMode === 'percentage') {
      const pct = parseFloat(val) || 0;
      num = budget > 0 ? Math.round((pct / 100) * budget) : 0;
    } else {
      num = val === '' ? '' : Math.max(0, Math.round(parseFloat(val) || 0));
    }
    
    setCategoryBudgets(prev => {
      const u = { ...prev };
      if (num === '' || (num === 0 && splitMode === 'amount')) delete u[cat]; 
      else u[cat] = num;
      return u;
    });
  };

  const handleUpdateOverallBudget = () => {
    if (onConfirm) {
      // We pass the new budget back via onConfirm so App.js can update eventData
      onConfirm({
        includeCategories,
        optOutCategories: defaultCategories.filter(c => !includeCategories.includes(c)),
        categoryBudgets,
        tweakNotes: tweakNotes.trim(),
        existingItems: existingItems.trim(),
        newTotalBudget: assignedTotal // Signal to parent to update the main budget
      });
      onClose();
    }
  };

  const handleSuggestSplit = () => {
    if (budget <= 0 || includeCategories.length === 0) return;
    setCategoryBudgets(suggestSplit(budget, includeCategories));
  };

  const handleClearAll = () => setCategoryBudgets({});

  const handleConfirm = () => {
    const prefs = {
      includeCategories,
      optOutCategories: defaultCategories.filter(c => !includeCategories.includes(c)),
      categoryBudgets,
      tweakNotes: tweakNotes.trim(),
      existingItems: existingItems.trim(),
    };
    if (assignedTotal > upperBound) {
      setShowOverBudgetConfirm(true);
      setPendingConfirm(() => () => { onConfirm(prefs); setShowOverBudgetConfirm(false); setPendingConfirm(null); });
    } else {
      onConfirm(prefs);
    }
  };

  useEffect(() => {
    document.body.classList.toggle('modal-open', isOpen);
    // Mark the body so global UI chrome (e.g. <BottomNav>) can hide itself
    // while a full-viewport modal is open. We use a data attribute alongside
    // the class so consumers can target it via either selector.
    if (isOpen) {
      document.body.setAttribute('data-modal-open', 'true');
    } else {
      document.body.removeAttribute('data-modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.removeAttribute('data-modal-open');
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  // Theme header data
  const themeName = theme?.name || eventData?.theme_name || null;
  const palette = (theme?.color_palette || []).slice(0, 6);
  const paletteHexes = palette.map(c => (typeof c === 'string' ? c : c?.hex || '#e5e7eb'));
  const heroGradient = paletteHexes.length >= 2
    ? `linear-gradient(135deg, ${paletteHexes[0]}dd, ${paletteHexes[1] || paletteHexes[0]}cc)`
    : 'linear-gradient(135deg, #7c3aed, #a855f7)';

  return createPortal((
    <div
      className="fixed inset-0 z-[2147483000] flex items-end justify-center overflow-hidden bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        // On small screens, dock to the bottom and consume the full viewport
        // height so the sticky footer (Generate Plan button) is always reachable
        // even when the on-screen keyboard or browser UI eats into vertical space.
        className="flex h-[100dvh] max-h-[100dvh] w-full max-w-lg flex-col overflow-hidden rounded-none bg-white shadow-2xl sm:h-auto sm:max-h-[94dvh] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >

        {/* ── GRADIENT HEADER ─────────────────────────────────── */}
        <div className="flex-shrink-0 relative overflow-hidden" style={{ background: heroGradient }}>
          {/* Color palette strip at top */}
          {paletteHexes.length > 0 && (
            <div className="flex h-1">
              {paletteHexes.map((hex, i) => (
                <div key={i} className="flex-1" style={{ backgroundColor: hex }} />
              ))}
            </div>
          )}

          <div className="px-5 pb-4 pt-4 sm:px-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-white/70">Customize</p>
                <h3 className="text-lg font-extrabold leading-tight text-white drop-shadow sm:text-xl">
                  {themeName || 'Your Theme'}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-white/75 sm:mt-1.5">
                  Choose categories, split your budget, and give the AI any special instructions.
                </p>
              </div>
              <ModalCloseButton onClick={onClose} />
            </div>
          </div>
        </div>

        {/* ── SCROLLABLE BODY ──────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">

          {/* 1. CATEGORY TOGGLES */}
          <div className="px-6 pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-gray-800">What to include</h4>
              <span className="text-xs text-gray-400">{includeCategories.length} of {defaultCategories.length} selected</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {defaultCategories.map(cat => {
                const meta = CATEGORY_META[cat];
                const on = includeCategories.includes(cat);
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => handleToggleInclude(cat)}
                    className={`relative flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all duration-150
                      ${on
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-gray-100 bg-gray-50 opacity-60 hover:opacity-80'
                      }`}
                  >
                    {/* Check indicator */}
                    <span className={`absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center transition-all
                      ${on ? 'bg-primary' : 'bg-gray-200'}`}>
                      {on && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <span className="text-2xl flex-shrink-0">{meta.icon}</span>
                    <div className="min-w-0">
                      <p className={`text-xs font-bold leading-tight ${on ? 'text-gray-900' : 'text-gray-500'}`}>{cat}</p>
                      <p className="text-[10px] text-gray-400 leading-tight mt-0.5 whitespace-normal break-words">{meta.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-100 mx-6" />

          {/* 2. BUDGET SECTION */}
          {budget > 0 && (
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-gray-800">Budget split <span className="font-normal text-gray-400">(optional)</span></h4>
                <div className="flex gap-2">
                  {Object.keys(categoryBudgets).length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="text-xs text-gray-400 hover:text-red-500 transition"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSuggestSplit}
                    className="text-xs font-semibold text-primary hover:text-primary/80 transition flex items-center gap-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Suggest split
                  </button>
                </div>
              </div>

              {/* Visual progress bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-gray-900">{formatPrice(assignedTotal)} <span className="text-xs font-normal text-gray-500">allocated</span></span>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-500">{formatPrice(budget)} total budget</span>
                      {assignedTotal > upperBound && (
                        <button 
                          type="button"
                          onClick={handleUpdateOverallBudget}
                          className="text-[11px] font-bold text-primary hover:underline flex items-center gap-1"
                        >
                          Update budget to {formatPrice(assignedTotal)}?
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center bg-gray-100 p-0.5 rounded-lg">
                    <button 
                      type="button"
                      onClick={() => setSplitMode('amount')}
                      className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${splitMode === 'amount' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      $
                    </button>
                    <button 
                      type="button"
                      onClick={() => setSplitMode('percentage')}
                      className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${splitMode === 'percentage' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      %
                    </button>
                  </div>
                </div>

                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${budgetPct}%`,
                      background: assignedTotal > upperBound
                        ? '#ef4444'
                        : assignedTotal >= lowerBound && assignedTotal > 0
                          ? '#22c55e'
                          : '#7c3aed',
                    }}
                  />
                </div>

                {/* Over-allocation warning banner */}
                {assignedTotal > upperBound && (
                  <div className="mt-4 bg-red-50 border border-red-100 rounded-xl p-3 flex items-start gap-3 animate-fade-in">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 text-red-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-bold text-red-800">Budget Limit Exceeded</p>
                      <p className="text-[10px] text-red-600 mt-0.5">Your allocation of {formatPrice(assignedTotal)} is higher than your set budget of {formatPrice(budget)}.</p>
                      <button 
                        type="button"
                        onClick={handleUpdateOverallBudget}
                        className="mt-2 text-[10px] font-bold text-red-700 bg-red-100/50 hover:bg-red-100 px-3 py-1 rounded-lg transition"
                      >
                        Increase event budget to {formatPrice(assignedTotal)}
                      </button>
                    </div>
                  </div>
                )}

                {!budgetStatus && remaining > 0 && assignedTotal > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1.5">
                    The remaining {formatPrice(remaining)} will be AI-allocated based on your theme's needs.
                  </p>
                )}
                {budgetStatus && assignedTotal <= upperBound && (
                  <div className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${budgetStatus.bg} ${budgetStatus.text} ${budgetStatus.ring}`}>
                    {budgetStatus.label}
                  </div>
                )}
              </div>

              {/* Per-category inputs — only for included categories */}
              <div className="space-y-2">
                {defaultCategories.filter(cat => includeCategories.includes(cat)).map(cat => {
                  const meta = CATEGORY_META[cat];
                  const allocated = categoryBudgets[cat] || '';
                  const pct = budget > 0 && allocated ? Math.min(100, Math.round((Number(allocated) / budget) * 100)) : 0;
                  return (
                    <div key={cat} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 border border-gray-100">
                      <span className="text-lg flex-shrink-0">{meta.icon}</span>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold text-gray-700">{cat}</span>
                      <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-primary/50 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 min-w-[160px] justify-end">
                      <div className="flex justify-end min-w-[50px]">
                        {pct > 0 && (
                          <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded animate-fade-in whitespace-nowrap">
                            {splitMode === 'percentage' ? formatPrice(allocated) : `${pct}%`}
                          </span>
                        )}
                      </div>
                      <div className="relative group">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium pointer-events-none transition-colors group-focus-within:text-primary">
                          {splitMode === 'percentage' ? '%' : currencySymbol}
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={splitMode === 'percentage' 
                            ? (budget > 0 ? (allocated ? Math.round((Number(allocated) / budget) * 100) : '') : '')
                            : allocated}
                          onChange={e => handleBudgetChange(cat, e.target.value)}
                          className="w-24 pl-7 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-center font-bold"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="h-px bg-gray-100 mx-6" />

          {/* 3. ITEMS I ALREADY HAVE */}
          <div className="px-6 py-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">📦</span>
              <h4 className="text-sm font-bold text-gray-800">Items I already have</h4>
              <span className="ml-auto text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Saves money</span>
            </div>
            <p className="text-xs text-gray-500 mb-2 leading-relaxed">
              List what you own or what the venue provides. The AI will skip these or supplement quantities as needed.
            </p>
            <textarea
              value={existingItems}
              onChange={e => setExistingItems(e.target.value)}
              rows={3}
              className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none transition bg-gray-50 focus:bg-white"
              placeholder="e.g., Venue provides tables & chairs. I own a speaker, 20 balloons, fairy lights."
            />
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-100 mx-6" />

          {/* 4. TWEAK THIS THEME */}
          <div className="px-6 py-4 pb-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">✏️</span>
              <h4 className="text-sm font-bold text-gray-800">Personalise this theme</h4>
              <span className="ml-auto text-[10px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">Boosts AI quality</span>
            </div>
            <textarea
              value={tweakNotes}
              onChange={e => setTweakNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none transition bg-gray-50 focus:bg-white"
              placeholder="e.g., Swap cake for cupcakes, keep decor eco-friendly, prefer outdoor games…"
            />
          </div>
        </div>

        {/* ── FOOTER ───────────────────────────────────────────── */}
        <div
          className="z-10 flex flex-shrink-0 items-center justify-between gap-3 rounded-b-none border-t border-gray-100 bg-white px-5 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.06)] sm:rounded-b-2xl sm:bg-gray-50/80 sm:px-6 sm:shadow-none"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <button
            onClick={onClose}
            className="shrink-0 px-3 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-xl transition sm:px-4"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 min-w-0 max-w-xs h-11 bg-gradient-to-r from-primary to-violet-600 text-white font-bold text-sm rounded-xl shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
            </svg>
            {getIntentCtaLabel(eventData?.focus_selection, 'finalReview')}
          </button>
        </div>
      </div>

      {/* ── OVER-BUDGET CONFIRMATION ──────────────────────────── */}
      {showOverBudgetConfirm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-gray-900 text-center mb-1">Over Budget</h3>
            <p className="text-sm text-gray-600 text-center mb-5">
              You've allocated <strong>{formatPrice(assignedTotal)}</strong> — that's{' '}
              <strong className="text-red-600">{formatPrice(overBy)}</strong> over your{' '}
              <strong>{formatPrice(budget)}</strong> budget
              {tolerance > 0 && ` (±${tolerance}% tolerance)`}.
              The AI will still generate the plan but totals may exceed your budget.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowOverBudgetConfirm(false); setPendingConfirm(null); }}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition"
              >
                Go back
              </button>
              <button
                onClick={() => pendingConfirm && pendingConfirm()}
                className="flex-1 px-4 py-2.5 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-xl transition"
              >
                Continue anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  ), document.body);
};

export default ThemeSelectionModal;
